import React from "react";
import PropTypes from "prop-types";

import QuickCaptureBtn from "../QuickCaptureBtn";
import { audioUrl } from "./audio";

// Every noun from the round, not just the misses. The end of a round is the one
// moment you have the whole set in front of you, so it's where reviewing,
// listening, marking known and capturing a word all belong.
export default function RoundResults({
  audioBaseUrl,
  colorCoding,
  colors,
  known,
  onToggleKnown,
  quickCapture,
  results,
}) {
  const knownWords = new Set(known);

  // A short clip per row - no loop or stop to manage here, unlike the card.
  const play = (noun) => {
    const audio = new Audio(audioUrl(audioBaseUrl, noun.written));
    audio.play().catch(() => {});
  };

  return (
    <table className="fna-results">
      <tbody>
        {results.map(({ noun, right }) => {
          const isKnown = knownWords.has(noun.word);

          return (
            <tr className={isKnown ? "fna-results-known" : null} key={noun.word}>
              <td className="fna-results-mark">
                <span
                  aria-label={right ? "Correct" : "Missed"}
                  className={`glyphicon glyphicon-${right ? "ok" : "remove"}`}
                />
              </td>

              <td
                className="fna-results-word"
                style={colorCoding ? { color: colors[noun.gender] } : null}
              >
                {noun.written}
              </td>

              {/* Visible by default - it's the thing you want when reviewing. */}
              <td className="fna-results-translation">{noun.translations.join("; ")}</td>

              <td className="fna-results-actions">
                {audioBaseUrl && (
                  <button
                    aria-label={`Play ${noun.written}`}
                    className="fna-results-btn"
                    onClick={() => play(noun)}
                    type="button"
                  >
                    <span className="glyphicon glyphicon-volume-up" />
                  </button>
                )}

                <button
                  className="btn btn-link btn-xs"
                  onClick={() => onToggleKnown(noun.word)}
                  type="button"
                >
                  {isKnown ? "Practice again" : "Known"}
                </button>

                {quickCapture && (
                  <QuickCaptureBtn
                    autoSubmitInitialText
                    className="fna-results-btn"
                    collectionsUrl={quickCapture.collectionsUrl}
                    demoEntries={quickCapture.demoEntries}
                    entriesUrl={quickCapture.entriesUrl}
                    importsUrl={quickCapture.importsUrl}
                    initialText={noun.word}
                    isPro={quickCapture.isPro}
                    key={noun.word}
                    targetLanguageEnglishName={quickCapture.targetLanguageEnglishName}
                    targetLanguageIso={quickCapture.targetLanguageIso}
                    targetLanguageName={quickCapture.targetLanguageName}
                    text={false}
                    title={`See ${noun.word} in context`}
                  />
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

RoundResults.defaultProps = { audioBaseUrl: null, quickCapture: null };

RoundResults.propTypes = {
  audioBaseUrl: PropTypes.string,
  colorCoding: PropTypes.bool.isRequired,
  colors: PropTypes.objectOf(PropTypes.string).isRequired,
  known: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggleKnown: PropTypes.func.isRequired,
  quickCapture: PropTypes.object,
  results: PropTypes.arrayOf(PropTypes.object).isRequired,
};
